import ConfirmationModal from 'components/Modals/ConfirmationModal';
import { usePerms } from 'contexts/PermsContext';
import { selectDeviceTypeMap } from 'features/integrations/state/selectors/integrationSelectors';
import { systemSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import useWindow from 'hooks/useWindow';
import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import {
  useAddConnectedDeviceMutation,
  useGetAllAvailableDeviceTypesQuery,
  useGetConnectedDevicesQuery,
  useRemoveConnectedDeviceMutation,
  useUpdateConnectedDeviceMutation,
} from 'services/devices/devices.api';
import {
  useFusionAddIntegrationMutation,
  useFusionDeleteIntegrationMutation,
  useFusionGetIntegrationsQuery,
  useFusionUpdateIntegrationMutation,
} from 'services/integrations/integrations.api';
import { TryParseString } from 'utils/parserUtils';
import Accordion from './components/Accordion';
import DeviceModal from './components/DeviceModal/DeviceModal';
import SystemModal from './components/SystemModal';
import Table from './components/Table';
import { getDeviceIndexMap, getFilteredDeviceTypes } from './utils/connectedSystemsUtils';

const ConnectedSystemsWeb = ({}) => {
  const { data: connectedDevices = [] } = useGetConnectedDevicesQuery();
  const [addConnectedDevice, { isLoading: isAddingDevice, isSuccess: addDeviceSuccess }] =
    useAddConnectedDeviceMutation();
  const [updateConnectedDevice, { isLoading: isUpdatingDevice, isSuccess: updateDeviceSuccess }] =
    useUpdateConnectedDeviceMutation();
  const [removeConnectedDevice] = useRemoveConnectedDeviceMutation();
  const [fusionAddIntegration] = useFusionAddIntegrationMutation();
  const [fusionUpdateIntegration] = useFusionUpdateIntegrationMutation();
  const [fusionDeleteIntegration] = useFusionDeleteIntegrationMutation();
  const { data: integrations = [] } = useFusionGetIntegrationsQuery();
  const { data: deviceTypes = {} } = useGetAllAvailableDeviceTypesQuery();
  const deviceTypeMap = useSelector(selectDeviceTypeMap);
  const perms = usePerms();
  const canViewUSGS = perms.testPermission(
    [8, 9, 10, 11, 12, 13],
    ['ViewUSGSMonitoringStationConnections'],
  );
  const { width, height } = useWindow();
  const [filteredIntegrations, setFilteredIntegrations] = useState(null);
  const [newEntity, setNewEntity] = useState(); // Used to determine if editing a system or device
  const [toEdit, setToEdit] = useState(); // Holds the System or Device being edited
  const [toDelete, setToDelete] = useState(false); // Holds the System or Device being deleted
  const [displayMsg, setDisplayMsg] = useState(); // Holds success/error messages
  const [loading, setLoading] = useState(false);
  const [systemRowIdEllipsisOpen, setSystemRowIdEllipsisOpen] = useState({});
  const [deviceRowIdEllipsisOpen, setDeviceRowIdEllipsisOpen] = useState({});
  const deviceIndexMap = getDeviceIndexMap(connectedDevices);

  const openEditFormForSystemOrDevice = useCallback((typeOfEntity, entityBeingEdited) => {
    setDisplayMsg(null);
    setNewEntity(typeOfEntity); // "system" or "device" (string)
    setToEdit(entityBeingEdited); // system or device object
  }, []);

  const prepareSystemOrDeviceForDeletion = useCallback((entity) => {
    setDisplayMsg(null);
    setToDelete({
      ...entity,
      name: entity?.system_name || entity?.alias || 'N/A',
      type: entity?.system_name ? 'system' : 'device',
    });
  }, []);

  const handleAddSystemAndDeviceButton = (entityBeingAdded) => {
    setDisplayMsg(null);
    setNewEntity(entityBeingAdded);
    setToEdit(null);
  };

  const handleAddAndEditSystem = async (newSystem) => {
    let res;
    setLoading(true);
    try {
      if (!toEdit) {
        try {
          res = await fusionAddIntegration(newSystem).unwrap(); // RTK Query mutation needs to be unwrapped to catch errors in try/catch
        } catch (error) {
          console.error('Error in handleAddAndEditSystem when adding new system:', error);
        }
      } else {
        try {
          res = await fusionUpdateIntegration(newSystem).unwrap(); // RTK Query mutation needs to be unwrapped to catch errors in try/catch
        } catch (error) {
          console.error('Error in handleAddAndEditSystem when editing system:', error);
        }
      }

      if (res.status === 200) {
        setToEdit(null);
        setNewEntity(false);
        setDisplayMsg({ type: 'message', message: 'Success!' });
      }
    } catch (err) {
      console.error('Error in resolveAddSystem:', err);
      setDisplayMsg({ type: 'error', message: res?.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  const handleAddAndEditDevice = async (newDevice) => {
    let res;
    const { device, ...newDeviceVar } = newDevice;
    const formattedNewDevice = { ...device, ...newDeviceVar };

    //TODO: In cases where there are no coordinates, broken coordinates or coordinates point at lat:0 Lng:0, have a location selector
    try {
      if (!toEdit) {
        // Handle edge case where coordinates are invalid
        if (!formattedNewDevice.coordinates || typeof formattedNewDevice.coordinates === 'string') {
          formattedNewDevice.coordinates = { lat: 0.0, lng: 0.0 };
        }
        //Handle edge case where location is invalid
        if (!formattedNewDevice.location || typeof formattedNewDevice.location === 'string') {
          formattedNewDevice.location = { lat: 0.0, lng: 0.0 };
        }
        await addConnectedDevice({ ...formattedNewDevice }).unwrap(); // RTK Query mutation needs to be unwrapped to catch errors in try/catch
      } else {
        //Formating info for update
        const updatedDeviceInfo = {
          id: formattedNewDevice?.id,
          node_id: formattedNewDevice?.node_id,
          new_node_id: Number(formattedNewDevice?.new_node_id),
          name: formattedNewDevice?.name,
          alias: formattedNewDevice?.alias,
          location: formattedNewDevice?.location,
          coordinates: TryParseString(formattedNewDevice?.coordinates),
          device_type: formattedNewDevice?.device_type,
          groups: formattedNewDevice?.groups || [],
        };

        await updateConnectedDevice(updatedDeviceInfo).unwrap(); // RTK Query mutation needs to be unwrapped to catch errors in try/catch
      }

      if (addDeviceSuccess || updateDeviceSuccess) {
        setToEdit(null);
        setNewEntity(null);
        setDisplayMsg({ type: 'message', message: 'Success!' });
      }
    } catch (err) {
      console.error('Error in resolveAddDevice:', err);
      setDisplayMsg({ type: 'error', message: res?.response?.data?.message });
    }
  };

  const handleCancelModal = () => setNewEntity(false);

  const handleDeleteDeviceAndSystem = async (confirmDeletion) => {
    try {
      if (confirmDeletion) {
        switch (toDelete.type) {
          case 'system':
            await fusionDeleteIntegration(toDelete).unwrap();
            break;
          case 'device':
            await removeConnectedDevice(toDelete).unwrap();
            break;
          default:
            break;
        }
      }

      setToDelete(null);
      setDisplayMsg({ type: 'message', message: 'Success!' });
    } catch (err) {
      console.error('Error in handleDeleteDeviceOrSystem:', err);
      setDisplayMsg({ type: 'error', message: err.data?.message ?? 'Delete failed' });
    }
  };

  useEffect(() => {
    if (!integrations) return;

    const filteredIntegrations = integrations
      .filter((system) => {
        //TODO: Only show systems that have at least one device type the user can view
        const filteredDeviceTypes = getFilteredDeviceTypes(
          system.system_type,
          deviceTypeMap,
          perms,
        );
        return Object.keys(filteredDeviceTypes).length > 0;
      })
      // hide USGS systems entirely if user lacks the specific view permission
      .filter((system) => (system.system_type === 'USGS' ? canViewUSGS : true))
      .filter((system) => systemSearchFilter(system, deviceTypes, connectedDevices));
    setFilteredIntegrations(filteredIntegrations);
  }, [integrations, deviceTypeMap, perms, deviceTypes, connectedDevices]);

  return (
    <div className="d-flex flex-column gap-3">
      {width >= 992 && (
        <div>
          <Table
            deviceIndexMap={deviceIndexMap}
            filteredIntegrations={filteredIntegrations}
            openEditFormForSystemOrDevice={openEditFormForSystemOrDevice}
            prepareSystemOrDeviceForDeletion={prepareSystemOrDeviceForDeletion}
            deviceRowIdEllipsisOpen={deviceRowIdEllipsisOpen}
            setDeviceRowIdEllipsisOpen={setDeviceRowIdEllipsisOpen}
            systemRowIdEllipsisOpen={systemRowIdEllipsisOpen}
            setSystemRowIdEllipsisOpen={setSystemRowIdEllipsisOpen}
          />
        </div>
      )}
      {width < 992 && (
        <div>
          <Accordion
            deviceIndexMap={deviceIndexMap}
            filteredIntegrations={filteredIntegrations}
            onEditSystem={openEditFormForSystemOrDevice}
            onDeleteSystem={prepareSystemOrDeviceForDeletion}
            onEditDevice={openEditFormForSystemOrDevice}
            onDeleteDevice={prepareSystemOrDeviceForDeletion}
            deviceRowIdEllipsisOpen={deviceRowIdEllipsisOpen}
            setDeviceRowIdEllipsisOpen={setDeviceRowIdEllipsisOpen}
            systemRowIdEllipsisOpen={systemRowIdEllipsisOpen}
            setSystemRowIdEllipsisOpen={setSystemRowIdEllipsisOpen}
          />
        </div>
      )}
      {/* Controls */}
      <div className="d-flex flex-column flex-md-row gap-3">
        {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditIntegrations']) && (
          <>
            <Button
              className="w-100 w-md-3xs py-3 py-md-4"
              onClick={() => handleAddSystemAndDeviceButton('system')}
              variant="primary"
            >
              New System
            </Button>
            <Button
              className="w-100 w-md-3xs py-3 py-md-4"
              onClick={() => handleAddSystemAndDeviceButton('device')}
              variant="primary"
            >
              New Device
            </Button>
          </>
        )}
      </div>
      {/* New System Modal */}
      {newEntity === 'system' && (
        <SystemModal
          onSubmit={handleAddAndEditSystem}
          onCancel={handleCancelModal}
          system={toEdit}
          loading={loading}
        />
      )}
      {/* New Device Modal */}
      {newEntity === 'device' && (
        <DeviceModal
          onSubmit={handleAddAndEditDevice}
          onCancel={handleCancelModal}
          device={toEdit}
          message={displayMsg}
          loading={isAddingDevice || isUpdatingDevice}
        />
      )}
      {/* Delete modal */}
      <ConfirmationModal
        show={toDelete}
        handleShow={() => handleDeleteDeviceAndSystem(false)}
        bodyTxt={`Are you sure you want to delete ${toDelete?.name} from the workplace?`}
        cancelBtnTxt="Cancel"
        confirmBtnTxt="Yes"
        cancelHandler={() => handleDeleteDeviceAndSystem(false)}
        confirmHandler={() => handleDeleteDeviceAndSystem(true)}
      />
    </div>
  );
};

export default ConnectedSystemsWeb;
